import React, { useEffect, useMemo, useState } from 'react';
import {
  GoogleMap,
  Marker,
  OverlayView,
  Polyline,
} from '@react-google-maps/api';
import { useSelector } from 'react-redux';
import type { LatLng, RouteDeviationVariant } from '@/apis/types';
import { getMutedMapStyles } from '@/common/GoogleMapStyle.js';
import { useGoogleMaps } from '@/components/GoogleMapsProvider';
import type { RootState } from '@/redux-toolkit/store';
import styles from './RouteDeviation.module.css';
import { isTicked } from './RouteDeviationVariantList';

interface RouteDeviationMapProps {
  variants: RouteDeviationVariant[];
  /** Trip IDs the user counts as normal; a group's line is highlighted when it holds any. */
  tickedTripIds: ReadonlySet<string>;
  pickup?: LatLng | null;
  delivery?: LatLng | null;
  pickupName?: string;
  /** The pickup's city, shown as `Origin · <city>` under its name. */
  pickupCity?: string;
  deliveryName?: string;
  currentPosition?: LatLng | null;
}

const MAP_STYLE = { width: '100%', height: '100%' };

// What the map shows when there is nothing to fit yet: the lower 48.
const FALLBACK_CENTER: LatLng = { lat: 39.5, lng: -98.35 };
const FALLBACK_ZOOM = 4;

// Same accents as the shipment preview map: green on dark tiles, the usual
// blue on light ones. Light pins are the design's black dot in a white ring.
const palette = (darkModeEnabled: boolean) => ({
  accent: darkModeEnabled ? '#34D399' : '#266EF1',
  unticked: darkModeEnabled ? '#9CA3AF' : '#8A8A8A',
  untickedFill: darkModeEnabled ? '#1F2126' : '#FFFFFF',
  untickedText: darkModeEnabled ? '#F2F3F5' : '#000000',
  origin: darkModeEnabled ? '#22C55E' : '#000000',
  destination: darkModeEnabled ? '#F97316' : '#000000',
  stroke: '#FFFFFF',
});

// A tick repeated along an otherwise invisible line draws it dashed: ticked
// routes get a bolder, longer dash than the grey ones.
const dash = (ticked: boolean) => ({
  path: 'M 0,-1 0,1',
  strokeOpacity: 1,
  scale: ticked ? 3 : 2,
});
const DASH_REPEAT = { ticked: '13px', unticked: '9px' };

// Labels sit beside their pin, vertically centred on it: the origin's to the
// left, the destination's to the right, as in the design.
const PIN_LABEL_GAP = 10;
const labelLeftOf = (width: number, height: number) => ({
  x: -width - PIN_LABEL_GAP,
  y: -height / 2,
});
const labelRightOf = (_width: number, height: number) => ({
  x: PIN_LABEL_GAP,
  y: -height / 2,
});

const toLatLng = ([lat, lng]: [number, number]): LatLng => ({ lat, lng });

const RouteDeviationMap: React.FC<RouteDeviationMapProps> = ({
  variants,
  tickedTripIds,
  pickup,
  delivery,
  pickupName,
  pickupCity,
  deliveryName,
  currentPosition,
}) => {
  const { isLoaded, loadError } = useGoogleMaps();
  const darkModeEnabled = useSelector(
    (state: RootState) => state.appearance.darkModeEnabled
  );
  const mapStyles = useMemo(
    () => getMutedMapStyles(darkModeEnabled),
    [darkModeEnabled]
  );
  // The starting view is a prop, not an option, so a theme switch restyles the
  // map without resetting the view the fit has set.
  const mapOptions = useMemo(
    () => ({
      styles: mapStyles,
      // The design shows a bare map; dragging and scroll zoom still work.
      disableDefaultUI: true,
    }),
    [mapStyles]
  );
  const colours = palette(darkModeEnabled);
  const [map, setMap] = useState<google.maps.Map | null>(null);

  const paths = useMemo(
    () => variants.map((variant) => variant.representative_path.map(toLatLng)),
    [variants]
  );

  // Fit on what is drawn, not on the ticks, so ticking never moves the map.
  const pickupLat = pickup?.lat;
  const pickupLng = pickup?.lng;
  const deliveryLat = delivery?.lat;
  const deliveryLng = delivery?.lng;
  useEffect(() => {
    if (!map) return;
    const bounds = new window.google.maps.LatLngBounds();
    let extended = 0;
    const extend = (point: LatLng) => {
      bounds.extend(point);
      extended += 1;
    };
    if (pickupLat != null && pickupLng != null)
      extend({ lat: pickupLat, lng: pickupLng });
    if (deliveryLat != null && deliveryLng != null)
      extend({ lat: deliveryLat, lng: deliveryLng });
    paths.forEach((path) => path.forEach(extend));
    if (extended > 0) map.fitBounds(bounds);
  }, [map, paths, pickupLat, pickupLng, deliveryLat, deliveryLng]);

  if (loadError) {
    return (
      <div className={styles.mapState}>
        Unable to load map. Please try again.
      </div>
    );
  }
  if (!isLoaded) {
    return <div className={styles.mapState}>Loading map...</div>;
  }

  const circle = (fill: string, stroke: string, scale: number) => ({
    path: window.google.maps.SymbolPath.CIRCLE,
    fillColor: fill,
    fillOpacity: 1,
    strokeColor: stroke,
    strokeWeight: 2,
    scale,
  });

  return (
    <GoogleMap
      mapContainerStyle={MAP_STYLE}
      options={mapOptions}
      center={FALLBACK_CENTER}
      zoom={FALLBACK_ZOOM}
      onLoad={setMap}
    >
      {variants.map((variant, index) => {
        const path = paths[index];
        if (path.length < 2) return null;
        const ticked = isTicked(variant, tickedTripIds);
        const color = ticked ? colours.accent : colours.unticked;
        return (
          <React.Fragment key={variant.label}>
            <Polyline
              path={path}
              options={{
                strokeColor: color,
                strokeOpacity: 0,
                zIndex: ticked ? 2 : 1,
                icons: [
                  {
                    icon: { ...dash(ticked), strokeColor: color },
                    repeat: ticked ? DASH_REPEAT.ticked : DASH_REPEAT.unticked,
                  },
                  ...(ticked
                    ? [
                        {
                          icon: {
                            path: window.google.maps.SymbolPath
                              .FORWARD_OPEN_ARROW,
                            strokeColor: color,
                            strokeOpacity: 1,
                            strokeWeight: 2,
                            scale: 2.5,
                          },
                          offset: '70%',
                        },
                      ]
                    : []),
                ],
              }}
            />
            <Marker
              position={path[Math.floor(path.length / 2)]}
              title={`Route ${variant.label}`}
              zIndex={ticked ? 3 : 2}
              icon={
                ticked
                  ? circle(color, colours.stroke, 8)
                  : circle(colours.untickedFill, color, 8)
              }
              label={{
                text: variant.label,
                color: ticked ? '#FFFFFF' : colours.untickedText,
                fontSize: '10px',
                fontWeight: '600',
              }}
            />
          </React.Fragment>
        );
      })}
      {pickup && (
        <Marker
          position={pickup}
          title='Pickup'
          icon={circle(colours.origin, colours.stroke, 6)}
        />
      )}
      {pickup && pickupName && (
        <OverlayView
          position={pickup}
          mapPaneName={OverlayView.OVERLAY_LAYER}
          getPixelPositionOffset={labelLeftOf}
        >
          <div className={`${styles.pinLabel} ${styles.pinLabelStart}`}>
            <span className={styles.pinName}>{pickupName}</span>
            <span className={styles.pinRole}>
              {pickupCity ? `Origin · ${pickupCity}` : 'Origin'}
            </span>
          </div>
        </OverlayView>
      )}
      {delivery && (
        <Marker
          position={delivery}
          title='Delivery'
          icon={circle(colours.destination, colours.stroke, 6)}
        />
      )}
      {delivery && deliveryName && (
        <OverlayView
          position={delivery}
          mapPaneName={OverlayView.OVERLAY_LAYER}
          getPixelPositionOffset={labelRightOf}
        >
          <div className={styles.pinLabel}>
            <span className={styles.pinName}>{deliveryName}</span>
            <span className={styles.pinRole}>Destination</span>
          </div>
        </OverlayView>
      )}
      {currentPosition && (
        <Marker
          position={currentPosition}
          title='Current position'
          icon={circle(colours.accent, '#FFFFFF', 7)}
        />
      )}
    </GoogleMap>
  );
};

export default RouteDeviationMap;
